import { Activity, ArrowUpRight, CircleDot, Clock3, Flame, Radio, ShieldCheck, Zap } from 'lucide-react'
import useSWR from 'swr'
import { Badge, EmptyState, Panel, Skeleton } from '../components/ui'
import { fetcher } from '../lib/api'
import { compactNumber, relativeTime } from '../lib/format'
import { useStream } from '../lib/stream'
import type { Feed } from '../types'

const eventLabel: Record<string, string> = {
  'feed.reachable': 'Source online', 'feed.blocked': 'Source blocked', 'feed.unreachable': 'Source unreachable',
  'article.new': 'New report', 'fact.new': 'New fact', 'fact.changed': 'Figure changed',
  'fact.conflict': 'Contradiction', 'fact.corrected': 'Correction', 'indexing.degraded': 'Indexing degraded',
}

export function LivePage() {
  const { data, isLoading } = useSWR<{ feeds: Feed[] }>('/v1/feeds', fetcher)
  const { events, connected } = useStream()
  const feeds = data?.feeds ?? []
  let active = 0; let blocked = 0; let followers = 0
  for (const feed of feeds) {
    if (feed.active) active++
    if (feed.reachability_status === 'CAPTCHA_BLOCKED' || feed.reachability_status === 'ANTI_BOT_BLOCKED') blocked++
    followers += feed.follower_count
  }
  return (
    <div className="page page--live">
      <header className="page-heading page-heading--hero">
        <div><p className="eyebrow"><Flame size={13} /> Operations pulse</p><h1>LIVE <span>/ now</span></h1><p>New events, changing facts, source confirmations, and corrections as Blaze sees them.</p></div>
        <Badge tone={connected ? 'good' : 'warn'} dot>{connected ? 'SSE connected' : 'Signal interrupted'}</Badge>
      </header>
      <section className="metric-grid" aria-label="Live source metrics">
        <Metric label="Active feeds" value={isLoading ? undefined : compactNumber(active)} detail="10-second polling" icon={<Radio />} tone="hot" />
        <Metric label="Signals this session" value={compactNumber(events.length)} detail="Realtime observations" icon={<Zap />} />
        <Metric label="Blocked sources" value={isLoading ? undefined : compactNumber(blocked)} detail={blocked ? 'Needs review' : 'Clear'} icon={<ShieldCheck />} tone={blocked ? 'warn' : 'good'} />
        <Metric label="Source follows" value={isLoading ? undefined : compactNumber(followers)} detail="Globally reused" icon={<Activity />} />
      </section>
      <div className="live-grid">
        <Panel.Root className="signal-feed">
          <Panel.Header><div><span className="panel-kicker"><CircleDot size={12} /> Incoming signal</span><Panel.Title>Intelligence stream</Panel.Title></div><span className="mono-label">LATEST 100</span></Panel.Header>
          <Panel.Body>
            {events.length ? <ol className="signal-list">{events.map((event) => (
              <li key={`${event.id}-${event.receivedAt.getTime()}`} className="signal-item">
                <span className="signal-item__rail"><i /><b /></span>
                <div className="signal-item__main"><div><Badge tone={event.type.includes('blocked') || event.type.includes('conflict') ? 'bad' : 'accent'}>{eventLabel[event.type] ?? event.type}</Badge><time>{relativeTime(event.receivedAt.toISOString())}</time></div><p>{describeEvent(event.data)}</p></div>
                <ArrowUpRight size={15} aria-hidden="true" />
              </li>
            ))}</ol> : <EmptyState heading="Listening for the next signal" description="The stream is connected. New source, article, fact, story, event, and indexing transitions will appear here." />}
          </Panel.Body>
        </Panel.Root>
        <aside className="live-aside">
          <Panel.Root>
            <Panel.Header><div><span className="panel-kicker">Source state</span><Panel.Title>Recently checked</Panel.Title></div></Panel.Header>
            <Panel.Body className="source-mini-list">
              {isLoading ? Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="source-mini-skeleton" />) : feeds.slice(0, 6).map((feed) => (
                <div className="source-mini" key={feed.id}><span className={`source-mini__status ${feed.active ? 'is-up' : 'is-down'}`} /><div><strong>{feed.name}</strong><small>{feed.publication || feed.category || 'Uncategorized'}</small></div><time>{relativeTime(feed.last_attempt_at)}</time></div>
              ))}
              {!isLoading && !feeds.length ? <p className="muted-copy">No sources admitted yet.</p> : null}
            </Panel.Body>
          </Panel.Root>
          <div className="timing-card"><Clock3 /><span><small>Polling guarantee</small><strong>10.000 seconds</strong></span><Badge tone="good" dot>Fixed</Badge></div>
        </aside>
      </div>
    </div>
  )
}

function Metric({ label, value, detail, icon, tone = '' }: { label: string; value?: string; detail: string; icon: React.ReactNode; tone?: string }) {
  return <article className={`metric-card metric-card--${tone}`}><span className="metric-card__icon">{icon}</span><p>{label}</p>{value === undefined ? <Skeleton className="metric-card__skeleton" /> : <strong>{value}</strong>}<small>{detail}</small></article>
}

function describeEvent(data: unknown) {
  if (!data || typeof data !== 'object') return 'Blaze observed a new intelligence transition.'
  const record = data as Record<string, unknown>
  return String(record.name ?? record.headline ?? record.title ?? record.canonical_url ?? 'Blaze observed a new intelligence transition.')
}

